import React from "react";
import { Result, Button, Typography } from "antd";

/**
 * 錯誤邊界。
 *
 * React 18 遇到未捕捉的錯誤會把整棵樹從根卸載 —— 沒有邊界時，任何一個元件出錯
 * 都會讓整站變成一片白，而且錯誤訊息跟著畫面一起消失，只能靠重現才查得到原因。
 * 營運儀表板的圖表卸載 TypeError 就是這樣變成「切頁白畫面」的。
 *
 * 放在版面的內容區裡而不是包住整個 App：側邊選單與標題列要留著，人才能直接切到
 * 別頁繼續做事，而不是被迫重新載入。
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * DefaultLayout 包在 Switch 外層 (App.js)，換頁不會重新掛載，邊界也就不會自己
   * 復原。少了這段，一頁出錯之後每一頁都會停在錯誤畫面，只能整頁重新載入。
   */
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey === state.resetKey) return null;
    return { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error, info) {
    // 完整的元件堆疊留在 devtools，畫面上只放看得懂的那一行
    console.error("[ErrorBoundary]", error, info && info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Result
        status="error"
        title="這個畫面出了問題"
        subTitle="其他頁面仍可正常使用，可從左側選單切換。若重新整理後依然如此，請把下方訊息提供給系統管理者。"
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            重新整理
          </Button>
        }
      >
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 0, wordBreak: "break-word", textAlign: "center" }}
        >
          {String((error && error.message) || error)}
        </Typography.Paragraph>
      </Result>
    );
  }
}
